/**
 * "This is a demo" — the honesty framing (SKE-4166, phase 7).
 *
 * WHY THIS IS PRODUCT-CRITICAL, NOT DECORATION
 * --------------------------------------------
 * The demo is deliberately indistinguishable from the product: it *is* the
 * approver SPA, on real media, with working buttons. Phase 4 then re-skins it
 * with a real company's name and logo on request. Two beliefs a visitor could
 * reasonably form from that are both false and both expensive:
 *
 *   "this is my client's actual account"  — it is a sample board on a seeded
 *                                           demo link, and after personalizing,
 *                                           branding we borrowed for the pitch.
 *   "I just published / approved something real" — every action is local to
 *                                           this browser and reaches nothing.
 *
 * So the disclosure is stated on screen at all times rather than left to a
 * landing page the ad traffic may never have read.
 *
 * SURVIVES PERSONALIZATION — by saying MORE, not less
 * ---------------------------------------------------
 * The one moment the framing matters most is the moment it would be easiest to
 * lose: the board wearing the visitor's own client's brand. The badge does not
 * merely persist through that, it changes copy to name the borrowing outright
 * ("styled as {brand}" / "a Sked Social demo wearing {brand}'s branding"), so
 * the more convincing the skin gets, the more explicit the label gets.
 *
 * PLACEMENT
 * ---------
 * Top-right, on every viewport. The bottom edge is already fully spoken for by
 * the demo's controls (gate / personalization bottom-left, Reset demo
 * bottom-right, the mobile role toggle, the mobile flip card), and the top
 * centre is the approver's own view switcher — the top-right corner of the
 * header is the one region free on both desktop and mobile.
 *
 * `z-[45]` puts it above the marketer rail (z-40) so a revealed rail can never
 * hide the disclosure, and below the dialog layer (z-50) so it never sits over
 * the gate or the CTA. `pointer-events-none` throughout: it is a statement, not
 * a control, and it must not intercept a click meant for the header beneath it.
 *
 * Tailwind only (styled-components are lint-blocked in this app); the only
 * motion is the shared load reveal, which is `motion-safe:` gated.
 */

import { cn } from '@dialogueconsulting/sked-ui/lib/utils'

import { isDemoMode } from './demoMode'
import { DEMO_REVEAL_DROP, demoRevealStyle } from './demoReveal'
import { demoFormat, demoStrings } from './demoStrings'
import { useDemoBrand } from './useDemoBrand'

const DemoHonestyBadge: React.FC = () => {
  // `useDemoBrand` already returns null on the reviewer path, so it is safe to
  // call before the guard below — which keeps the hook order fixed whichever
  // path this renders on.
  const brand = useDemoBrand()

  if (!isDemoMode()) {
    return null
  }

  const label = brand
    ? demoFormat('honestyBadgeBrandedLabel', { brand: brand.name })
    : demoStrings.honestyBadgeLabel

  const note = brand
    ? demoFormat('honestyNoteBranded', { brand: brand.name })
    : demoStrings.honestyNote

  return (
    <div
      role="note"
      aria-label={demoStrings.honestyBadgeAriaLabel}
      data-testid="demo-honesty-badge"
      style={demoRevealStyle(1)}
      className={cn(
        'pointer-events-none fixed right-3 top-3 z-[45] flex max-w-[15rem] flex-col items-end gap-1 text-right',
        'sm:right-4 sm:top-4',
        // On the desktop two-pane (>= DEMO_TWO_PANE_MIN_WIDTH) the disclosure
        // lives inside the client browser chrome (see ClientFrame), so the
        // floating badge would only duplicate it and collide with the team
        // pane — hide it there. It remains the disclosure on every narrower
        // viewport (mobile flip, role toggle).
        'min-[1200px]:hidden',
        DEMO_REVEAL_DROP
      )}
    >
      <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-neutral-700 shadow-sm backdrop-blur">
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500"
        />
        {label}
      </span>

      <span className="rounded bg-white/80 px-1.5 py-0.5 text-[10px] leading-snug text-neutral-500 backdrop-blur">
        {note}
      </span>
    </div>
  )
}

export default DemoHonestyBadge
